import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import { useTran, useFormDirty } from "@/hooks";
import { AppDialog, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  BU_CATEGORY,
  LOCATION_OPTIONS,
  ORGANIZATION_REGION_OPTIONS,
} from "@/lib/constants";
import { useOrganizationStore } from "../hooks";
import { validateOrganizationUnitForm } from "../schemas";
import { companyStore } from "@/features/company/stores/company.store";

const DEFAULT_FORM = {
  companyId: "",
  unitLevel: "level_1",
  unitLevelName: "",
  name: "",
  code: "",
  parentId: "",
  region: "",
  country: "",
  address: "",
};

const LEVEL_REGEX = /^level_(\d+)$/;

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const normalizeUnitLevel = (value) => {
  if (!value) return "level_1";
  const raw = String(value).trim();

  if (LEVEL_REGEX.test(raw)) return raw.toLowerCase();

  const m = /^level\s*(\d+)$/i.exec(raw);
  if (m) return `level_${m[1]}`;

  // "2"
  if (/^\d+$/.test(raw)) return `level_${raw}`;

  return "level_1";
};

const buildLevelOptions = (maxLevel) => {
  const safeMax = Math.min(3, Math.max(1, (maxLevel || 0) + 1));
  return Array.from({ length: safeMax }, (_, index) => {
    const level = index + 1;
    return { key: `level_${level}`, label: `Level ${level}`, level };
  });
};

// Walks up the parentId chain from directParentId to collect ancestor IDs
// ordered from level_1 to level_(targetLevel-2), used to pre-fill cascading selectors in edit mode.
const computeInitialAncestorIds = (directParentId, itemsList) => {
  if (!directParentId || !Array.isArray(itemsList)) return [];
  const directParent = itemsList.find((u) => u.id === String(directParentId));
  if (!directParent) return [];
  const chain = [];
  let cursor = directParent;
  while (cursor && cursor.parentId) {
    const parent = itemsList.find((u) => u.id === String(cursor.parentId));
    if (!parent || parent.unitLevel === "parent_company") break;
    chain.unshift(parent.id);
    cursor = parent;
  }
  return chain;
};

const getCountriesByRegion = (regionKey, allOptions) => {
  if (!regionKey) return [];
  if (regionKey === "global") return allOptions;
  const hasRegions = allOptions.some(
    (option) =>
      Array.isArray(option.regions) || Array.isArray(option.regionKey),
  );
  if (!hasRegions) return allOptions;
  return allOptions.filter((c) => {
    const regions = Array.isArray(c.regions)
      ? c.regions
      : Array.isArray(c.regionKey)
        ? c.regionKey
        : [];
    return regions.includes(regionKey);
  });
};

const toRegionList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) =>
        String(entry || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);
  }
  if (typeof value !== "string") return [];
  return value
    .split(/[;|]/)
    .flatMap((entry) => entry.split(","))
    .map((entry) =>
      String(entry || "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);
};

const OrganizationUnitDialog = observer(
  ({
    open,
    mode = "create",
    initialValues = {},
    onSubmit,
    onOpenChange,
    adminMode = false,
  }) => {
    const { t } = useTran();
    const { items, maxLevel, countryOptions, regionOptions, saving } =
      useOrganizationStore();
    const isAdmin = Boolean(adminMode);
    const isEdit = mode === "edit";
    const [form, setForm] = useState(DEFAULT_FORM);
    const [errors, setErrors] = useState({});
    const [ancestorIds, setAncestorIds] = useState([]);

    const formValues = useMemo(
      () => ({ form, ancestorIds }),
      [form, ancestorIds],
    );
    const isDirty = useFormDirty(formValues, open);

    useEffect(() => {
      if (!open) return;
      if (!isAdmin) return;
      void companyStore.fetchPublicCompanies().catch(() => {});
    }, [open, isAdmin]);

    useEffect(() => {
      if (!open) return;
      setErrors({});
      setForm({
        ...DEFAULT_FORM,
        ...initialValues,
        companyId: String(initialValues?.companyId || DEFAULT_FORM.companyId),
        unitLevel: normalizeUnitLevel(
          initialValues?.unitLevel || DEFAULT_FORM.unitLevel,
        ),
        unitLevelName: initialValues?.unitLevelName || "",
        parentId: initialValues?.parentId || "",
        region: initialValues?.region || "",
        country: initialValues?.country || "",
        name: initialValues?.name || "",
        code: initialValues?.code || "",
        address: initialValues?.address || "",
      });
      const level = getLevelNumber(
        normalizeUnitLevel(initialValues?.unitLevel || DEFAULT_FORM.unitLevel),
      );
      if (level && level > 2 && initialValues?.parentId) {
        setAncestorIds(
          computeInitialAncestorIds(initialValues.parentId, items),
        );
      } else {
        setAncestorIds([]);
      }
    }, [open, initialValues]); // eslint-disable-line react-hooks/exhaustive-deps -- items intentionally omitted to avoid resetting user selections on refetch

    const activeCompanyId = isAdmin ? String(form.companyId || "") : null;

    const maxLevelForActiveCompany = useMemo(() => {
      if (!isAdmin) return maxLevel;

      const cid = String(activeCompanyId || "");
      if (!cid) return 0;

      return (items || []).reduce((mx, u) => {
        const ucid = String(u?.companyId ?? "");
        if (ucid !== cid) return mx;
        const lv = getLevelNumber(u?.unitLevel);
        return lv && lv > mx ? lv : mx;
      }, 0);
    }, [isAdmin, activeCompanyId, items, maxLevel]);

    const levelOptions = useMemo(() => {
      const base = buildLevelOptions(maxLevelForActiveCompany).map((o) => ({
        ...o,
        label: t(o.label),
      }));
      const current = normalizeUnitLevel(form.unitLevel);
      const exists = base.some((o) => o.key === current);
      if (!exists) {
        const lv = getLevelNumber(current);
        if (lv) base.push({ key: current, label: t(`Level ${lv}`), level: lv });
      }
      return base.sort((a, b) => (a.level || 0) - (b.level || 0));
    }, [t, maxLevelForActiveCompany, form.unitLevel]);

    // const regionOptions = useMemo(
    //   () =>
    //     ORGANIZATION_REGION_OPTIONS.map((option) => ({
    //       ...option,
    //       label: t(option.label),
    //     })),
    //   [t],
    // );
    const businessTypeOptions = useMemo(
      () =>
        BU_CATEGORY.map((option) => ({
          value: option.key ?? option.value,
          label: t(option.value ?? option.label ?? option.key),
        })),
      [t],
    );
    const countrySource = useMemo(() => {
      const source = countryOptions.length ? countryOptions : LOCATION_OPTIONS;
      return (source || [])
        .map((option) => {
          const key =
            option.key ??
            option.value ??
            option.code ??
            option.id ??
            option.name;
          if (!key) return null;
          return {
            key,
            label:
              option.label ??
              option.value ??
              option.name ??
              option.key ??
              String(key),
            regions: toRegionList(option.regions ?? option.regionKey),
          };
        })
        .filter(Boolean);
    }, [countryOptions]);
    const resolvedRegionOptions = useMemo(() => {
      const apiRegions = (regionOptions || [])
        .map((option) => {
          const key = String(option?.key ?? option?.value ?? "")
            .trim()
            .toLowerCase();
          if (!key) return null;
          return {
            key,
            label: String(option?.value ?? option?.label ?? key),
          };
        })
        .filter(Boolean);

      if (apiRegions.length > 0) {
        return apiRegions.map((option) => ({
          ...option,
          label: t(option.label),
        }));
      }

      return ORGANIZATION_REGION_OPTIONS.map((option) => ({
        ...option,
        label: t(option.label),
      }));
    }, [regionOptions, t]);
    const regionCountryOptions = useMemo(() => {
      const list = getCountriesByRegion(form.region, countrySource);
      const seen = new Set();
      return list.filter((item) => {
        if (seen.has(item.key)) return false;
        seen.add(item.key);
        return true;
      });
    }, [form.region, countrySource]);

    const selectedLevel = getLevelNumber(form.unitLevel);

    const parentChoices = useMemo(() => {
      if (!selectedLevel || selectedLevel <= 1) return [];
      const parentLevel = selectedLevel - 1;

      return (items || [])
        .filter((u) => {
          if (getLevelNumber(u.unitLevel) !== parentLevel) return false;
          // Server rejects creating a unit under an inactive parent tree —
          // don't offer one as a choice in the first place.
          if (u.status === "inactive") return false;

          if (isAdmin) {
            const ucid = String(u?.companyId ?? "");
            if (!activeCompanyId || ucid !== String(activeCompanyId))
              return false;
          }

          return true;
        })
        .map((u) => ({ id: u.id, name: u.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [items, selectedLevel, isAdmin, activeCompanyId]);

    const companyOptionsList = useMemo(() => {
      const opts = companyStore.companyOptions || [];
      return opts.map((o) => ({
        key: String(o.value),
        label: o.label,
      }));
    }, [companyStore.companyOptions]);

    const handleInput = (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSelectChange = (field) => (input) => {
      const value = input?.target ? input.target.value : input;
      handleInput(field, value);
    };

    const handleRegionChange = (input) => {
      const value = input?.target ? input.target.value : input;
      setForm((prev) => {
        if ((prev.region ?? "") === (value ?? "")) return prev;
        return { ...prev, region: value ?? "", country: "" };
      });
    };

    const handleCompanyChange = (input) => {
      const value = input?.target ? input.target.value : input;
      setForm((prev) => ({
        ...prev,
        companyId: String(value || ""),
        unitLevel: "level_1",
        parentId: "",
      }));
      setAncestorIds([]);
    };

    const handleUnitLevelChange = (input) => {
      const value = input?.target ? input.target.value : input;
      setForm((prev) => ({
        ...prev,
        unitLevel: normalizeUnitLevel(value),
        parentId: "",
      }));
      setAncestorIds([]);
    };

    const handleAncestorChange = (index, value) => {
      setAncestorIds((prev) => {
        const next = prev.slice(0, index);
        next[index] = value || "";
        return next;
      });
      setForm((prev) => ({ ...prev, parentId: "" }));
    };

    const getUnitsAtLevel = (levelNum, filterParentId) =>
      (items || [])
        .filter((u) => {
          if (getLevelNumber(u.unitLevel) !== levelNum) return false;
          // Same reasoning as parentChoices — an inactive unit's descendants
          // are inactive too, so excluding it here prunes the whole inactive
          // branch from the cascading ancestor pickers as well.
          if (u.status === "inactive") return false;
          if (isAdmin) {
            const ucid = String(u?.companyId ?? "");
            if (!activeCompanyId || ucid !== String(activeCompanyId))
              return false;
          }
          if (filterParentId) {
            return String(u.parentId ?? "") === String(filterParentId);
          }
          return true;
        })
        .map((u) => ({ id: u.id, name: u.name }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const submit = async (event) => {
      event.preventDefault();
      const parentChoicesForValidation =
        selectedLevel && selectedLevel > 2
          ? getUnitsAtLevel(selectedLevel - 1, ancestorIds[selectedLevel - 3])
          : parentChoices;
      const nextErrors = validateOrganizationUnitForm(form, {
        parentChoicesLength: parentChoicesForValidation.length,
        t,
      });
      if (isAdmin && !String(form.companyId || "").trim()) {
        nextErrors.companyId = t("Company is required");
      }

      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      const level = getLevelNumber(form.unitLevel);
      const normalizedParentId =
        level && level > 1
          ? form.parentId && String(form.parentId).trim().length > 0
            ? form.parentId
            : null
          : null;

      const payload = {
        name: form.name,
        code: form.code,
        unitLevel: form.unitLevel,
        unitLevelName: form.unitLevelName || null,
        parentId: normalizedParentId,
        region: form.region || null,
        country: form.country || null,
        address: form.address || null,
      };

      if (isAdmin && mode === "create") {
        payload.companyId = form.companyId;
      }

      await onSubmit?.(payload);
    };

    const disableLevelInAdminCreateUntilCompany =
      isAdmin && mode === "create" && !activeCompanyId;

    const isRegionCountryRequired = form.unitLevel !== "level_1";

    return (
      <AppDialog
        open={open}
        onOpenChange={onOpenChange}
        isDirty={isDirty}
        title={
          mode === "create"
            ? t("Add organization unit")
            : t("Edit organization unit")
        }
        description={t("Define the unit details, hierarchy, and location.")}
        size="lg"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              form="organization-unit-form"
              disabled={saving}
            >
              {saving
                ? t("Saving...")
                : mode === "create"
                  ? t("Create")
                  : t("Update")}
            </Button>
          </>
        }
      >
        <form
          id="organization-unit-form"
          onSubmit={submit}
          className="space-y-5"
        >
          {isAdmin ? (
            <SelectField
              id="companyId"
              label={t("Company")}
              required
              value={form.companyId || undefined}
              onChange={handleCompanyChange}
              options={companyOptionsList}
              placeholder={t("Select company")}
              error={errors.companyId}
              emptyLabel={t("No companies available")}
              disabled={isEdit}
            />
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="unit-name">
                {t("Unit Name")}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="unit-name"
                value={form.name}
                onChange={(event) => handleInput("name", event.target.value)}
                placeholder={t("Enter unit name")}
                className={errors.name ? "border-destructive" : ""}
              />
              {errors.name ? (
                <p className="text-xs text-destructive">{errors.name}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-code">
                {t("Unit Code")}
                {(!selectedLevel || selectedLevel <= 2) && (
                  <span className="text-destructive">*</span>
                )}
              </Label>
              <Input
                id="org-code"
                value={form.code}
                onChange={(event) =>
                  handleInput("code", event.target.value.replace(/\s+/g, ""))
                }
                placeholder={t("Enter unit code")}
                className={errors.code ? "border-destructive" : ""}
              />
              {errors.code ? (
                <p className="text-xs text-destructive">{errors.code}</p>
              ) : null}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <SelectField
                id="unitLevel"
                label={t("Unit Level")}
                value={form.unitLevel || undefined}
                onChange={handleUnitLevelChange}
                options={levelOptions}
                placeholder={t("Select unit level")}
                error={errors.unitLevel}
                disabled={disableLevelInAdminCreateUntilCompany}
              />
              {disableLevelInAdminCreateUntilCompany ? (
                <p className="text-xs text-muted-foreground">
                  {t("Select a company first")}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit-level-name">{t("Unit Level Name")}</Label>
              <Input
                id="unit-level-name"
                value={form.unitLevelName}
                onChange={(event) =>
                  handleInput("unitLevelName", event.target.value)
                }
                placeholder={t("E.g., HQ or Subsidiary")}
              />
            </div>
          </div>

          {form.unitLevel !== "level_1" ? (
            <div className="grid gap-4 md:grid-cols-2">
              {selectedLevel && selectedLevel > 2 ? (
                <>
                  {Array.from({ length: selectedLevel - 2 }, (_, i) => {
                    const levelNum = i + 1;
                    const filterParentId = i > 0 ? ancestorIds[i - 1] : null;
                    const opts = getUnitsAtLevel(levelNum, filterParentId);
                    return (
                      <SelectField
                        key={`ancestor-level-${levelNum}-${activeCompanyId || "na"}`}
                        id={`ancestor-level-${levelNum}`}
                        label={t(`Level ${levelNum} Parent`)}
                        value={ancestorIds[i] || undefined}
                        onChange={(input) => {
                          const value = input?.target
                            ? input.target.value
                            : input;
                          handleAncestorChange(i, value);
                        }}
                        options={opts.map((o) => ({
                          key: o.id,
                          label: o.name,
                        }))}
                        placeholder={t("Select level unit", {
                          level: levelNum,
                        })}
                        disabled={i > 0 && !ancestorIds[i - 1]}
                        emptyLabel={t("No units available")}
                      />
                    );
                  })}
                  <SelectField
                    key={`parent-${form.unitLevel}-${activeCompanyId || "na"}`}
                    id="parentId"
                    label={t(`Level ${selectedLevel - 1} Parent`)}
                    required
                    value={form.parentId || undefined}
                    onChange={handleSelectChange("parentId")}
                    options={getUnitsAtLevel(
                      selectedLevel - 1,
                      ancestorIds[selectedLevel - 3],
                    ).map((o) => ({ key: o.id, label: o.name }))}
                    placeholder={t("Select level unit", {
                      level: selectedLevel - 1,
                    })}
                    error={errors.parentId}
                    disabled={!ancestorIds[selectedLevel - 3]}
                    emptyLabel={t("No parent units available")}
                  />
                </>
              ) : (
                <SelectField
                  key={`parent-${form.unitLevel}-${activeCompanyId || "na"}`}
                  id="parentId"
                  label={t("Parent Unit")}
                  required
                  value={form.parentId || undefined}
                  onChange={handleSelectChange("parentId")}
                  options={parentChoices.map((option) => ({
                    key: option.id,
                    label: option.name,
                  }))}
                  placeholder={t("Select parent unit")}
                  error={errors.parentId}
                  disabled={!selectedLevel || selectedLevel <= 1}
                  emptyLabel={t("No parent units available")}
                />
              )}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <SelectField
              id="region"
              label={t("Region")}
              required={isRegionCountryRequired}
              value={form.region || undefined}
              onChange={handleRegionChange}
              options={resolvedRegionOptions}
              placeholder={t("Select region")}
              error={errors.region}
            />
            <SelectField
              id="country"
              label={t("Country")}
              required={isRegionCountryRequired}
              value={form.country || undefined}
              onChange={handleSelectChange("country")}
              options={regionCountryOptions}
              placeholder={t("Select country")}
              error={errors.country}
              emptyLabel={t("No countries available")}
              disabled={!form.region}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="org-address">{t("Address")}</Label>
            <Textarea
              id="org-address"
              value={form.address}
              onChange={(event) => handleInput("address", event.target.value)}
              placeholder={t("Enter address (optional)")}
              rows={3}
            />
          </div>
        </form>
      </AppDialog>
    );
  },
);

export default OrganizationUnitDialog;
